import { useRef} from 'react';
import { useLocation } from 'react-router-dom';
import { intersection } from 'lodash';
import moment from "moment";
import { resolveSchoolId, substituteSchoolId } from './resolveSchoolId';
//import log from 'loglevel';

export const useQuery = () => { 
	  return new URLSearchParams(useLocation().search);
};

export const isArrayWithLength = (arr) => {
 return (Array.isArray(arr) && arr.length)
}

export const isAllowedRoute = (route, user) => {
 if (!isArrayWithLength(route.roles) && !isArrayWithLength(route.permissions)) return true;
 else return intersection(route.roles, user.roles).length + intersection(route.permissions, user.permissions).length;
}

export const getAllowedRoutes = (routes, user) => {
 return routes.filter(({ roles, permissions }) => {
		 if(!isArrayWithLength(roles) && !isArrayWithLength(permissions)) return true;
		 else return intersection(roles, user.roles).length + intersection(permissions, user.permissions).length;
 });
}

export const getHomeBrowserRoute = (routes, user) => {
	return getHomeHashRoute(routes, user);
}

// Route templates in PrivateRoutesConfig use `:schoolId` (Ciclo 7). Home is one of the callers
// that actually navigates to the returned path, so anything school-scoped needs the id resolved
// before it leaves this helper — otherwise the URL bar would show a literal `":schoolId"`.
export const getHomeHashRoute = (routes, user) => {

	let homeRoutes = routes.filter(({ home }) => {
		if (!home) return false;
		else if(!isArrayWithLength(home)) return false;
		else return intersection(home, user.roles).length;
	});

	if (isArrayWithLength(homeRoutes)) {
		const path = homeRoutes[0].path;
		if (path.includes(':schoolId')) {
			const schoolId = resolveSchoolId(user);
			// If we somehow can't resolve (no scope) we still return the template — the caller
			// will hit a routing miss. Better than returning /home silently and hiding the state.
			return schoolId ? substituteSchoolId(path, schoolId) : path;
		}
		return path;
	}

	return "/home";
}


export const getAllowedNavbarOptions = (options, user) => {
	 
	 return options.filter(({ roles, permissions }) => {
		 if(!isArrayWithLength(roles) && !isArrayWithLength(permissions)) return true;
		 else return intersection(roles, user.roles).length + intersection(permissions, user.permissions).length;
	 });
}


export const loadScript = (id, url, callback) => {
	  
	  const existingScript = document.getElementById(id);
	  
	  if (!existingScript) {
	    const script = document.createElement('script');
	    script.src = url;
	    script.id = id;
	    
	    document.body.appendChild(script);
	    //document.getElementsByTagName("head")[0].appendChild(script);
	    
	    script.onload = () => { 
	      if (callback) callback();
	    };
	    
	    script.onerror = () =>{ /* TODO: handle error */ }
	    
	  } else {
		  if (callback) callback();
	  }

};

export const promiseWithTimeout = (promise, milliseconds) => {

    const timeout =  new Promise((resolve, reject) =>
        setTimeout(() => 
            reject(`Promise timeout reached (limit: ${milliseconds} ms)`), 
            	milliseconds));

    return Promise.race([
        timeout, 
        promise
    ]);
};


export const parseInputDate = (inputDate) => {
    
    	let resultDate;
		
		if (inputDate) {
    		if (moment.isMoment(inputDate) || inputDate instanceof Date) {
				resultDate = moment.utc(inputDate);
			} else {	
				
				//resultDate = moment.utc(inputDate, 'DD/MM/YYYY');
				resultDate = moment.utc(inputDate, 'L');
				
				//If invalid set a custom flag: inputDate
				if (!resultDate.isValid())
					resultDate = moment.invalid({inputDate: inputDate});
			}
    		
    	}
 
    	return resultDate;
    }

 
 export const compareJSON = (obj1, obj2) => {

  var ret = {};
  for(var i in obj2) {
    if(!obj1.hasOwnProperty(i) || obj2[i] !== obj1[i]) {
      ret[i] = obj2[i];
    }
  }
  return ret;
}

export const niceBytes = (x) => {
 const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let l = 0, n = parseInt(x, 10) || 0;
  while(n >= 1024 && ++l){
      n = n/1024;
  }
  return(n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + units[l]);
}

export const isAsyncSupported = () => {
	let isAsync = true;

	try {
  		eval('async () => {}');
	} catch (e) {
  		if (e instanceof SyntaxError)
    		isAsync = false;
  		else
    		throw e; // throws CSP error
	}
	
	return isAsync;
}

export const isObject = (val) => {
  if (val === null) { return false;}
    return ( (typeof val === 'function') || (typeof val === 'object') );
}

/**
 * Booleano vindo do backend — aceita `true` e a STRING `"true"`.
 *
 * ⚠️ Os resources do Play serializam boolean como string (`Boolean.toString`
 * em QuestionResource.proprietary/active, QuestionAnswerOptionResource
 * .isCorrectAnswer, QuestionResolutionStepResource.isFinalAnswer, …). Em JS
 * `"false"` é TRUTHY, então `!!campo` é sempre `true` — bug silencioso: o
 * dado está certo no banco e errado na tela.
 *
 * Existiam 3 grafias pra mesma pergunta (`x === true || x === 'true'`,
 * `JSON.parse(x)`, `!!x`) e só as duas primeiras funcionavam. Uma só agora.
 */
export const isTrue = (val) => val === true || val === 'true';

export const Regex = {
	DOUBLE: '^[0-9]{0,}(\\.[0-9]{1,})?$',
	CURRENCY: '^[0-9]{1,8}(\\.[0-9]{0,2})?$',
	NEGATIVE_CURRENCY: '^-?[0-9]{1,8}(\\.[0-9]{0,2})?$',
	CPF: '^\\d{3}\\x2E\\d{3}\\x2E\\d{3}\\x2D\\d{2}$',
	CNPJ: '^\\d{2}\\x2E\\d{3}\\x2E\\d{3}\\x2f\\d{4}\\x2D\\d{2}$',
	MOBILE_PHONE: '^\\+[1-9]\\d{1,14}$'
}


export const currencySymbol = (locale, currency) => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });

  let symbol;
  formatter.formatToParts(0).forEach(({ type, value }) => {
    if (type === 'currency') {
      symbol = value;
    }
  });

  return symbol;
};

export const formatCurrency = (value, locale, currency) => {
  
  const formatter = Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  });

  return formatter.format(value);
};

export const formatNumber = (value, locale, options) => {
  const formatter = Intl.NumberFormat(locale, options);

  return formatter.format(value);
};

export const formatPercent = (value, locale) => {
  
  const formatter = Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 2
  });

  return formatter.format(value);
};

export const formatDate = (inputDate, outputFormat) => {
    
    	let result;
		
		if (inputDate) {
			result = moment(inputDate).format(outputFormat);
    	} 
 
    	return result;
    }

 

export const getAllFiles = (r) => {
 		let files = {};
  		r.keys().forEach((item, index) => { files[item.replace('./', '')] = r(item); });
 		return files
}

// Function to remove accents for comparison
export const getNormalizedString = (str) => {
		return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
	};
	
export const CAN_USE_DOM =
  typeof window !== "undefined" &&
  typeof window.document !== "undefined" &&
  typeof window.document.createElement !== "undefined"

  
export const cloneObject = (obj) => {
		return JSON.parse(JSON.stringify(obj));
}; 
  
export const useDoubleClick = (onClick, onDbClick, delay = 300) => {
  const timePassed= useRef(0);
  return (e) => {
   if (e.detail === 1) {
     setTimeout(() => {
       if (Date.now() - timePassed.current >= delay) {
         onClick();
       }
     }, delay)
   }

   if (e.detail === 2) {
     timePassed.current = Date.now();
     onDbClick();
   }
 }
}

String.prototype.format = function () {
    const args = arguments;
    return this.replace(/{(\d+)}/g, function (match, number) {
        return typeof args[number] != 'undefined'
            ? args[number]
            : match;
    });
};


export const readFileAsDataURL = async (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = (e) => reject(e)
    reader.readAsDataURL(file)
  })

export const getRandomInteger = (min, max) => {
	min = Math.ceil(min); // Ensure min is an integer
  	max = Math.floor(max); // Ensure max is an integer
  	
  	return Math.floor(Math.random() * (max - min + 1)) + min;
}; 


export const sortByName = (a,b) => {		
  		const nameA = a.name.toUpperCase(); // ignore upper and lowercase
  		const nameB = b.name.toUpperCase(); // ignore upper and lowercase
  		
  		if (nameA < nameB) {
    		return -1;
  		}
  		if (nameA > nameB) {
    		return 1;
  		}

  		// names must be equal
  		return 0;
 	}
  
export const sortByOrder = (a,b) => {		
  		return parseInt(a.order)-parseInt(b.order);
 	}
  
/*export async function readFileAsDataURL(file) {
    let result_base64 = await new Promise((resolve) => {
        let fileReader = new FileReader();
        fileReader.onload = (e) => resolve(fileReader.result);
        fileReader.readAsDataURL(file);
    });

    return result_base64;
}*/
  
/**
 * Trunca no MEIO, preservando começo e fim.
 *
 * ⚠️ Ciclo 7 · 2026-08-09 — nomes internos de material carregam a informação
 * distintiva nas DUAS pontas: "E24 Geometria Plana — Parte III (p.335)". Cortar
 * no fim ("E24 Geometria Plana — Par…") esconde justamente a parte que
 * diferencia uma edição da outra.
 *
 * Usado no atalho "Usar «nome»" do item de plano, onde o professor precisa
 * reconhecer QUAL material é antes de copiar o nome.
 */
export function middleEllipsis(text, max = 48) {
	if (!text || text.length <= max) return text;
	const keep = Math.floor((max - 1) / 2);
	return `${text.slice(0, keep).trimEnd()}…${text.slice(-keep).trimStart()}`;
}
